import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Upload, Video, Music, FileText, Link, Clock, Users, BookOpen, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseManagement = ({ user }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'wellness',
    benefits: [''],
    thumbnail_url: ''
  });

  const [lessonForm, setLessonForm] = useState({
    title: '',
    content_type: 'text',
    content: '',
    duration_minutes: 0,
    order: 0
  });

  useEffect(() => {
    fetchCourses();
    fetchAnalytics();
  }, []);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/courses/my-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/courses/analytics/educator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const benefits = courseForm.benefits.filter(b => b.trim() !== '');
      
      await axios.post(`${API_URL}/courses`, {
        ...courseForm,
        benefits
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Course created successfully');
      setShowCreateModal(false);
      setCourseForm({ title: '', description: '', category: 'wellness', benefits: [''], thumbnail_url: '' });
      fetchCourses();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to create course');
    }
  };

  const handleUpdateCourse = async (courseId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/courses/${courseId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course updated');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deleted');
      fetchCourses();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/courses/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setLessonForm(prev => ({
        ...prev,
        content: response.data.file_url
      }));
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    if (!selectedCourse) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/courses/${selectedCourse.id}/lessons`, lessonForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Lesson added successfully');
      setShowLessonModal(false);
      setLessonForm({ title: '', content_type: 'text', content: '', duration_minutes: 0, order: 0 });
      fetchCourses();
    } catch (error) {
      toast.error('Failed to add lesson');
    }
  };

  const handleDeleteLesson = async (courseId, lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/courses/${courseId}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lesson deleted');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  const addBenefit = () => {
    setCourseForm(prev => ({
      ...prev,
      benefits: [...prev.benefits, '']
    }));
  };

  const updateBenefit = (index, value) => {
    setCourseForm(prev => ({
      ...prev,
      benefits: prev.benefits.map((b, i) => i === index ? value : b)
    }));
  };

  const removeBenefit = (index) => {
    setCourseForm(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={16} />;
      case 'audio': return <Music size={16} />;
      case 'external_url': return <Link size={16} />;
      default: return <FileText size={16} />;
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="management-page">
      {/* Analytics Section */}
      {analytics && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--color-primary)' }}>{analytics.total_courses}</div>
            <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Total Courses</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--color-success)' }}>{analytics.published_courses}</div>
            <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Published</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--color-info)' }}>{analytics.total_views}</div>
            <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Total Views</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--color-warning)' }}>{analytics.total_completions}</div>
            <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Completions</div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h2>My Courses</h2>
        <button className="gradient-btn" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} /> Create Course
        </button>
      </div>

      {/* Courses List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {courses.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
            <p style={{ color: 'var(--text-tertiary)' }}>No courses yet. Create your first course!</p>
          </div>
        ) : (
          courses.map(course => (
            <div key={course.id} className="card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>{course.title}</h3>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: course.category === 'wellness' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                      color: course.category === 'wellness' ? 'var(--color-success)' : 'var(--color-secondary)'
                    }}>
                      {course.category === 'wellness' ? '🧘 Wellness' : '📚 Skill Based'}
                    </span>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: course.is_published ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                      color: course.is_published ? 'var(--color-success)' : 'var(--color-warning)'
                    }}>
                      {course.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p style={{ margin: '0 0 12px 0', color: 'var(--text-tertiary)', fontSize: '14px' }}>
                    {course.description.length > 150 ? course.description.substring(0, 150) + '...' : course.description}
                  </p>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <span><Clock size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.total_duration_minutes} min</span>
                    <span><BookOpen size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.lessons?.length || 0} lessons</span>
                    <span><Eye size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.view_count} views</span>
                    <span><Users size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{course.completion_count} completions</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => handleUpdateCourse(course.id, { is_published: !course.is_published })}
                    className="btn-secondary"
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    {course.is_published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    onClick={() => { setSelectedCourse(course); setShowLessonModal(true); }}
                    className="gradient-btn"
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    <Plus size={14} /> Add Lesson
                  </button>
                  <button
                    onClick={() => handleDeleteCourse(course.id)}
                    style={{ padding: '8px', background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}
                  >
                    <Trash2 size={18} />
                  </button>
                  <button
                    onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                    style={{ padding: '8px', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    {expandedCourse === course.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded Lessons View */}
              {expandedCourse === course.id && (
                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: 'var(--text-primary)' }}>Lessons</h4>
                  {course.lessons?.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No lessons added yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {course.lessons?.map((lesson, idx) => (
                        <div key={lesson.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '8px'
                        }}>
                          <GripVertical size={16} style={{ color: 'var(--text-muted)' }} />
                          <span style={{ 
                            width: '24px', 
                            height: '24px', 
                            borderRadius: '50%', 
                            background: 'var(--color-primary)', 
                            color: 'white', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {idx + 1}
                          </span>
                          {getContentIcon(lesson.content_type)}
                          <span style={{ flex: 1, fontSize: '14px', color: 'var(--text-primary)' }}>{lesson.title}</span>
                          <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{lesson.duration_minutes} min</span>
                          <button
                            onClick={() => handleDeleteLesson(course.id, lesson.id)}
                            style={{ padding: '4px', background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Benefits */}
                  {course.benefits?.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: 'var(--text-primary)' }}>Benefits</h4>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {course.benefits.map((benefit, idx) => (
                          <li key={idx} style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{benefit}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Course Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <h3>Create New Course</h3>
            <form onSubmit={handleCreateCourse}>
              <div className="form-group">
                <label>Course Title</label>
                <input
                  type="text"
                  value={courseForm.title}
                  onChange={e => setCourseForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter course title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={courseForm.description}
                  onChange={e => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your course"
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={courseForm.category}
                  onChange={e => setCourseForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="wellness">🧘 Wellness (Maps to Self-Care)</option>
                  <option value="skill_based">📚 Skill Based (Maps to R&D/Learning)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Course Benefits</label>
                {courseForm.benefits.map((benefit, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={benefit}
                      onChange={e => updateBenefit(idx, e.target.value)}
                      placeholder={`Benefit ${idx + 1}`}
                    />
                    {courseForm.benefits.length > 1 && (
                      <button type="button" onClick={() => removeBenefit(idx)} style={{ padding: '8px', background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addBenefit} className="btn-secondary" style={{ padding: '8px 16px', fontSize: '13px' }}>
                  <Plus size={14} /> Add Benefit
                </button>
              </div>

              <div className="form-group">
                <label>Thumbnail URL (Optional)</label>
                <input
                  type="url"
                  value={courseForm.thumbnail_url}
                  onChange={e => setCourseForm(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="gradient-btn">Create Course</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Lesson Modal */}
      {showLessonModal && selectedCourse && (
        <div className="modal-overlay" onClick={() => setShowLessonModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <h3>Add Lesson to "{selectedCourse.title}"</h3>
            <form onSubmit={handleAddLesson}>
              <div className="form-group">
                <label>Lesson Title</label>
                <input
                  type="text"
                  value={lessonForm.title}
                  onChange={e => setLessonForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter lesson title"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Content Type</label>
                  <select
                    value={lessonForm.content_type}
                    onChange={e => setLessonForm(prev => ({ ...prev, content_type: e.target.value, content: '' }))}
                  >
                    <option value="text">📝 Text</option>
                    <option value="video">🎬 Video (Upload)</option>
                    <option value="audio">🎵 Audio (Upload)</option>
                    <option value="external_url">🔗 External URL (YouTube, etc.)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Duration (minutes)</label>
                  <input
                    type="number"
                    value={lessonForm.duration_minutes}
                    onChange={e => setLessonForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Content</label>
                {lessonForm.content_type === 'text' ? (
                  <textarea
                    value={lessonForm.content}
                    onChange={e => setLessonForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter lesson content"
                    rows={6}
                    required
                  />
                ) : lessonForm.content_type === 'external_url' ? (
                  <input
                    type="url"
                    value={lessonForm.content}
                    onChange={e => setLessonForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                    required
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      accept={lessonForm.content_type === 'video' ? 'video/*' : 'audio/*'}
                      onChange={e => handleFileUpload(e, lessonForm.content_type)}
                      style={{ marginBottom: '8px' }}
                    />
                    {uploading && <p style={{ color: 'var(--color-info)', fontSize: '14px' }}>Uploading...</p>}
                    {lessonForm.content && (
                      <p style={{ color: 'var(--color-success)', fontSize: '14px' }}>✓ File uploaded successfully</p>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowLessonModal(false)}>Cancel</button>
                <button type="submit" className="gradient-btn" disabled={uploading}>Add Lesson</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement;
